import { useMutation, useQueryClient } from '@tanstack/react-query'
import Cookies from 'js-cookie'
import { useStateMachine } from 'little-state-machine'

import { useLocale } from 'src/contexts/LocaleContext'
import { graphQLClient } from 'src/data/graphql/graphql-client'
import {
  useCartCreateMutation,
  useCartLinesAddMutation as generatedUseCartLinesAddMutation,
} from 'src/data/graphql-generated'
import { useCartQuery } from 'src/hooks/useCartQuery'
import { useTimer } from 'src/hooks/useTimer'
import { updateShowMinicart } from 'src/store/storeActions'

import type { Cart, CartLineInput, CartWarning, UserError } from 'src/data/graphql-generated'

// * jean says 7 seconds is the way
const MINICART_HIDE_IN_MILLISECONDS = 7000
export const SHOW_ADDED_TO_CART_IN_MILLISECONDS = MINICART_HIDE_IN_MILLISECONDS

export type CartLinesAddMutationFn = (variables: {
  cartLines: CartLineInput[]
}) => Promise<{ cart: Cart; userErrors: UserError[] }>

export const useCartLinesAddMutation = () => {
  const queryClient = useQueryClient()
  const { langCode } = useLocale()
  const { data: existingCart } = useCartQuery()
  const { setTimer } = useTimer(MINICART_HIDE_IN_MILLISECONDS)
  const { mutateAsync: createCart } = useCartCreateMutation(graphQLClient)
  const { mutateAsync: cartLinesAdd } = generatedUseCartLinesAddMutation(graphQLClient)
  const { actions } = useStateMachine({ updateShowMinicart })

  const mutationFn: CartLinesAddMutationFn = async (variables) => {
    const newOrUpdatedCart = existingCart
      ? await cartLinesAdd({
          cartId: existingCart.id,
          lines: variables.cartLines,
          lang: langCode,
        })
      : await createCart({
          lines: variables.cartLines,
          lang: langCode,
        })

    const [{ cart, warnings, userErrors }]: Array<{ cart: Cart; warnings: CartWarning[]; userErrors: UserError[] }> =
      Object.values(newOrUpdatedCart)

    return { cart, warnings, userErrors }
  }

  return useMutation({
    mutationFn,
    onSuccess: (data: { cart: Cart; userErrors: UserError[] }) => {
      if (data.userErrors.length) {
        return
      }

      Cookies.set('storefront_cart_id', data.cart.id, {
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
      })
      queryClient.setQueryData(['cart'], data.cart)

      actions.updateShowMinicart({ showMinicart: true })
      setTimer(() => actions.updateShowMinicart({ showMinicart: false }))
    },
  })
}
